import { ToggleButton, ToggleButtonGroup } from '@mui/material'
import { LifecycleState } from 'src/v3/core/types'

export type LifecycleFilterValue = 'all' | 'preArchive' | 'archive'

type Props = {
  value: LifecycleFilterValue
  onChange: (value: LifecycleFilterValue) => void
  counts?: { all?: number; preArchive?: number; archive?: number }
  orientation?: 'horizontal' | 'vertical'
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
}

let LifecycleFilter = ({
  value,
  onChange,
  counts,
  orientation = 'horizontal',
  size = 'small',
  fullWidth = false,
}: Props) => (
  <ToggleButtonGroup
    color="primary"
    size={size}
    value={value}
    exclusive
    onChange={(_, next) => next && onChange(next)}
    aria-label="Filter events by lifecycle state"
    orientation={orientation}
    fullWidth={fullWidth}
  >
    <ToggleButton value="all" sx={{ whiteSpace: 'nowrap' }}>
      All{counts?.all != null ? ` (${counts.all})` : ''}
    </ToggleButton>
    <ToggleButton value="preArchive" sx={{ whiteSpace: 'nowrap' }}>
      Pre-archive{counts?.preArchive != null ? ` (${counts.preArchive})` : ''}
    </ToggleButton>
    <ToggleButton value="archive" sx={{ whiteSpace: 'nowrap' }}>
      Archived{counts?.archive != null ? ` (${counts.archive})` : ''}
    </ToggleButton>
  </ToggleButtonGroup>
)

let stateOf = (item: unknown): LifecycleState | undefined =>
  (item as { lifecycleState?: LifecycleState })?.lifecycleState

// Apply the lifecycle visibility rule to a list of items keyed off
// `lifecycleState`. Under 'all', archive is hidden (pre-archive and active
// remain visible). Under 'preArchive' / 'archive', only matching items show.
// Items without `lifecycleState` are treated as active.
export let filterByLifecycle = <T,>(
  items: T[],
  filter: LifecycleFilterValue,
): T[] => {
  if (filter === 'preArchive') {
    return items.filter((i) => stateOf(i) === 'preArchive')
  }
  if (filter === 'archive') {
    return items.filter((i) => stateOf(i) === 'archive')
  }
  return items.filter((i) => stateOf(i) !== 'archive')
}

export let lifecycleCounts = <T,>(items: T[]) =>
  items.reduce(
    (acc, item) => {
      let s = stateOf(item)
      return {
        all: acc.all + (s !== 'archive' ? 1 : 0),
        preArchive: acc.preArchive + (s === 'preArchive' ? 1 : 0),
        archive: acc.archive + (s === 'archive' ? 1 : 0),
      }
    },
    { all: 0, preArchive: 0, archive: 0 },
  )

export default LifecycleFilter
